import React, { Dispatch, SetStateAction } from 'react';
import { BookingStep, WidgetConfiguration } from '../shared/hooks/booking-types';
import { BookingType } from '../shared/hooks/useBookingState';
import { Service } from '../shared/hooks/useServiceSelection';

// Import the step components
import LocationStep from '../location/LocationStep';
import ProviderStep from '../provider/ProviderStep';
import BookingConfirmation from './BookingConfirmation';
import CategorySelection from './categories/CategorySelection';
import Confirmation from './Confirmation';
import CustomerInfo from './customer/CustomerInfo';
import { CustomerInfoType } from '../shared/hooks/useCustomerInfo';
import DateTimeSelectionWrapper from './datetime/DateTimeSelectionWrapper';
import ServiceSelection from './services/ServiceSelection';

interface BookingStepRendererProps {
  currentStep: BookingStep;
  widgetConfiguration: WidgetConfiguration | null;

  // State values
  selectedServices: Service[];
  setSelectedServices: Dispatch<SetStateAction<Service[]>>;
  selectedProvider: any;
  setSelectedProvider: Dispatch<SetStateAction<any>>;
  selectedCategories: any;
  setSelectedCategories: Dispatch<SetStateAction<any>>;
  booking: BookingType | null;
  selectedDate: Date | null;
  setSelectedDate: Dispatch<SetStateAction<Date | null>>;
  selectedTime: string | null;
  setSelectedTime: Dispatch<SetStateAction<string | null>>;
  selectedLocation: any;
  setSelectedLocation: (location: any) => void;  // This is a custom handler
  selectedTimeZone: string;
  serviceProviderMap: any;
  setServiceProviderMap: Dispatch<SetStateAction<any>>;
  customerInfo: CustomerInfoType;
  setCustomerInfo: Dispatch<SetStateAction<CustomerInfoType>>;

  // Step configuration
  steps: Array<{
    name: string;
    step: BookingStep;
    icon: JSX.Element;
    nodeData?: any;
  }>;

  // Navigation functions
  nextStep: () => void;
  prevStep: () => void;

  // Other handlers
  onCompletedBooking: (booking: BookingType) => void;
  resetState: () => void;
}

const BookingStepRenderer: React.FC<BookingStepRendererProps> = ({
  currentStep,
  widgetConfiguration,
  selectedServices,
  setSelectedServices,
  selectedProvider,
  setSelectedProvider,
  selectedCategories,
  setSelectedCategories,
  booking,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  selectedLocation,
  setSelectedLocation,
  selectedTimeZone,
  serviceProviderMap,
  setServiceProviderMap,
  customerInfo,
  setCustomerInfo,
  steps,
  nextStep,
  prevStep,
  onCompletedBooking,
  resetState
}) => {
  // Skip location step if not activated
  if (currentStep === BookingStep.LOCATION_STEP && !widgetConfiguration?.isLocationsActivated) {
    return null;
  }

  // Skip provider step if not activated
  if (currentStep === BookingStep.PROVIDER_STEP && !widgetConfiguration?.isProvidersActivated) {
    return null;
  }

  if (currentStep === BookingStep.CATEGORIES_STEP && !widgetConfiguration?.isCategoriesActivated) {
    return null;
  }

  // Find the current step configuration from the steps array
  const currentStepConfig = steps.find(s => s.step === currentStep);

  // If we don't have configuration for this step, return null
  if (!currentStepConfig) {
    return null;
  }
  // Extract the node data for the current step
  const nodeData = currentStepConfig.nodeData || null;
  switch (currentStep) {
    case BookingStep.LOCATION_STEP:
      return (
        <LocationStep
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          onNext={nextStep}
          onPrev={() => console.log('hit')}
          nodeData={nodeData}
        />
      );
    case BookingStep.CATEGORIES_STEP:
      return (
        <CategorySelection
          widgetConfiguration={widgetConfiguration}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          onNext={nextStep}
          onPrev={() => console.log('hit')}
          nodeData={nodeData}
        />
      );
    case BookingStep.SERVICE_SELECTION:
      return (
        <ServiceSelection
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          widgetConfiguration={widgetConfiguration}
          selectedCategories={selectedCategories}
          onNext={nextStep}
          onPrev={prevStep}
          nodeData={nodeData}
        />
      );
    case BookingStep.PROVIDER_STEP:
      return (
        <ProviderStep
          selectedServices={selectedServices}
          //@ts-ignore
          selectedProvider={selectedProvider}
          selectedLocation={selectedLocation}
          setSelectedProvider={setSelectedProvider}
          setServiceProviderMap={setServiceProviderMap}
          serviceProviderMap={serviceProviderMap}
          onNext={nextStep}
          onPrev={prevStep}
          nodeData={nodeData}
        />
      );
    case BookingStep.DATE_TIME_SELECTION:
      return (
        <DateTimeSelectionWrapper
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedServices={selectedServices}
          setSelectedTime={setSelectedTime}
          selectedTime={selectedTime}
          selectedTimeZone={selectedTimeZone}
          selectedLocation={selectedLocation}
          onNext={nextStep}
          onPrev={prevStep}
          nodeData={nodeData}
        />
      );
    case BookingStep.CUSTOMER_INFO:
      return (
        <CustomerInfo
          customerInfo={customerInfo}
          setCustomerInfo={setCustomerInfo}
          onNext={nextStep}
          onPrev={prevStep}
          nodeData={nodeData}
        />
      );
    case BookingStep.CONFIRMATION:
      return (
        <Confirmation
          location={selectedLocation}
          selectedTime={selectedTime}
          selectedServices={selectedServices}
          selectedDateTime={selectedDate}
          selectedProvider={selectedProvider}
          customerInfo={customerInfo}
          widgetConfiguration={widgetConfiguration}
          serviceProviderMap={serviceProviderMap}
          timeZone={selectedTimeZone}
          onCompletedBooking={onCompletedBooking}
          onPrev={prevStep}
          nodeData={nodeData}
        />
      );
    case BookingStep.BOOKING_CONFIRMED:
      if (booking && selectedDate && selectedTime) {
        return (
          <BookingConfirmation
            location={selectedLocation}
            timeZone={selectedTimeZone}
            booking={booking}
            selectedServices={selectedServices}
            //@ts-ignore
            selectedProvider={selectedProvider}
            selectedDateTime={new Date(`${selectedDate.toDateString()} ${selectedTime}`)}
            customerInfo={customerInfo}
            widgetConfiguration={widgetConfiguration}
            resetState={resetState}
            serviceProviderMap={serviceProviderMap}
            nodeData={nodeData}
          />
        );
      }
      return null;
    default:
      return null;
  }
};

export default BookingStepRenderer;
